/**
 * EventNotificationsRow
 *
 * Reminders/notifications section.
 * Shows list of reminders with add button.
 */

import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import type { EventReminder } from '@/types/events';

type EventNotificationsRowProps = {
  reminders: EventReminder[];
  onAdd: () => void;
  onPressDate: (id: string) => void;
  onPressTime: (id: string) => void;
  onRemove: (id: string) => void;
  palette: typeof Colors.light;
};

function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes);
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function EventNotificationsRow({
  reminders,
  onAdd,
  onPressDate,
  onPressTime,
  onRemove,
  palette,
}: EventNotificationsRowProps) {
  return (
    <View style={styles.container}>
      <View style={styles.iconContainer}>
        <Ionicons name="notifications-outline" size={18} color={palette.textMuted} />
      </View>
      <View style={styles.content}>
        {reminders.length === 0 ? (
          <TouchableOpacity
            onPress={onAdd}
            style={[
              styles.addButton,
              { borderColor: palette.tint, backgroundColor: palette.tint + '15' },
            ]}
          >
            <Ionicons name="add" size={16} color={palette.tint} />
            <Text style={[styles.addLabel, { color: palette.tint, fontFamily: getBodyFont() }]}>
              Add notification
            </Text>
          </TouchableOpacity>
        ) : (
          <>
            {reminders.map(reminder => (
              <View key={reminder.id} style={styles.reminderRow}>
                <TouchableOpacity
                  onPress={() => onPressDate(reminder.id)}
                  style={[styles.reminderChip, { backgroundColor: palette.cardBackground }]}
                >
                  <Text style={[styles.reminderText, { color: palette.text, fontFamily: getBodyFont() }]}>
                    {formatDate(reminder.date)}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => onPressTime(reminder.id)}
                  style={[styles.reminderChip, { backgroundColor: palette.cardBackground }]}
                >
                  <Text style={[styles.reminderText, { color: palette.text, fontFamily: getBodyFont() }]}>
                    {formatTime(reminder.time)}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => onRemove(reminder.id)} style={styles.removeButton}>
                  <Ionicons name="close-circle" size={18} color={palette.textMuted} />
                </TouchableOpacity>
              </View>
            ))}
            <TouchableOpacity onPress={onAdd} style={[styles.addMoreButton, { borderColor: palette.tint }]}>
              <Ionicons name="add" size={16} color={palette.tint} />
              <Text style={[styles.addMoreLabel, { color: palette.tint, fontFamily: getBodyFont() }]}>
                Add another
              </Text>
            </TouchableOpacity>
          </>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm + 2, // 10px
  },
  iconContainer: {
    width: 40,
    alignItems: 'center',
    paddingTop: Spacing.xs,
  },
  content: {
    flex: 1,
    gap: Spacing.sm,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.xs,
    alignSelf: 'flex-start',
    paddingVertical: Spacing.sm,
    paddingHorizontal: Spacing.md,
    borderRadius: BorderRadius.xl,
    borderWidth: 1.5,
  },
  addLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
  reminderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  reminderChip: {
    paddingHorizontal: Spacing.sm + 2, // 10px
    paddingVertical: Spacing.sm, // normalized from 6
    borderRadius: BorderRadius.sm, // normalized from 6
  },
  reminderText: {
    fontSize: 14,
  },
  removeButton: {
    padding: Spacing.xs,
  },
  addMoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.xs,
    alignSelf: 'flex-start',
    paddingVertical: Spacing.xs + 2,
    paddingHorizontal: Spacing.md,
    borderRadius: BorderRadius.xl,
    borderWidth: 1,
  },
  addMoreLabel: {
    fontSize: 14,
  },
});
